import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database, Download, Trash2, RefreshCw, AlertCircle, CheckCircle, Save } from 'lucide-react';
import { logActivity } from '../lib/activity';

interface Backup {
  id: string;
  created_at: string;
  description: string;
  backup_size: number;
}

export default function BackupManagement() {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('backups')
        .select('id, created_at, description, backup_size')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBackups(data || []);
    } catch (error: any) {
      console.error('Error loading backups:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const exportData: any = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        data: {}
      };

      const tables = [
        'revenue_entries',
        'hidden_revenue',
        'fixed_expenses'
      ];

      for (const table of tables) {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        exportData.data[table] = data || [];
      }

      const backupSize = new Blob([JSON.stringify(exportData)]).size;
      await supabase
        .from('backups')
        .insert([{
          user_id: user.id,
          backup_data: exportData,
          backup_size: backupSize,
          description: `Handmatige backup - ${new Date().toLocaleString('nl-NL')}`
        }]);

      setMessage({ type: 'success', text: 'Backup succesvol aangemaakt' });
      await logActivity('Backup aangemaakt', 'Een handmatige backup is aangemaakt');
      loadBackups();
    } catch (error: any) {
      console.error('Create backup error:', error);
      setMessage({ type: 'error', text: `Fout bij aanmaken backup: ${error.message}` });
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (backupId: string) => {
    if (!confirm('WAARSCHUWING: Weet je zeker dat je deze backup wilt terugzetten?\n\nDit zal data uit de backup toevoegen of bijwerken in je huidige account.')) {
      return;
    }

    if (!confirm('LAATSTE BEVESTIGING: Ben je er absoluut zeker van?\n\nDit proces kan niet ongedaan gemaakt worden. Maak eerst een nieuwe backup van je huidige data als je deze wilt behouden.')) {
      return;
    }

    setRestoring(backupId);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const { data: backup, error: backupError } = await supabase
        .from('backups')
        .select('backup_data')
        .eq('id', backupId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (backupError) throw backupError;
      if (!backup) throw new Error('Backup niet gevonden');

      const importData = backup.backup_data as any;

      if (!importData.version || !importData.data) {
        throw new Error('Ongeldige backup data');
      }

      const tableOrder = ['revenue_entries', 'hidden_revenue', 'fixed_expenses'];

      for (const tableName of tableOrder) {
        if (importData.data[tableName] && importData.data[tableName].length > 0) {
          const records = importData.data[tableName].map((record: any) => {
            const { id, user_id, created_at, updated_at, ...recordData } = record;
            return { ...recordData, user_id: user.id };
          });

          if (tableName === 'revenue_entries' || tableName === 'hidden_revenue') {
            for (const record of records) {
              const { data: existing } = await supabase
                .from(tableName)
                .select('id')
                .eq('user_id', user.id)
                .eq('date', record.date)
                .maybeSingle();

              if (existing) {
                await supabase
                  .from(tableName)
                  .update(record)
                  .eq('id', existing.id);
              } else {
                await supabase
                  .from(tableName)
                  .insert([record]);
              }
            }
          } else {
            const { error } = await supabase
              .from(tableName)
              .insert(records);

            if (error && error.code !== '23505') {
              throw error;
            }
          }
        }
      }

      setMessage({ type: 'success', text: 'Backup succesvol hersteld!' });
      await logActivity('Backup hersteld', `Backup ${backupId} is succesvol hersteld`);

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Restore error:', error);
      setMessage({ type: 'error', text: `Fout bij herstellen: ${error.message}` });
    } finally {
      setRestoring(null);
    }
  };

  const downloadBackup = async (backupId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const { data: backup, error } = await supabase
        .from('backups')
        .select('backup_data, created_at')
        .eq('id', backupId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (!backup) throw new Error('Backup niet gevonden');

      const blob = new Blob([JSON.stringify(backup.backup_data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date(backup.created_at).toISOString().split('T')[0];
      a.download = `taxibase-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Backup gedownload' });
    } catch (error: any) {
      console.error('Download error:', error);
      setMessage({ type: 'error', text: `Fout bij downloaden: ${error.message}` });
    }
  };

  const deleteBackup = async (backupId: string) => {
    if (!confirm('Weet je zeker dat je deze backup wilt verwijderen? Dit kan niet ongedaan gemaakt worden.')) {
      return;
    }

    setDeleting(backupId);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const { error } = await supabase
        .from('backups')
        .delete()
        .eq('id', backupId)
        .eq('user_id', user.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Backup verwijderd' });
      await logActivity('Backup verwijderd', `Backup ${backupId} is verwijderd`);
      loadBackups();
    } catch (error: any) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: `Fout bij verwijderen: ${error.message}` });
    } finally {
      setDeleting(null);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Database className="h-5 w-5 text-gray-300" />
          <h2 className="text-base font-semibold text-white">Server Backups</h2>
        </div>
        <button
          type="button"
          onClick={loadBackups}
          disabled={loading}
          className="p-2 text-gray-400 hover:text-white transition-colors duration-200"
          title="Ververs lijst"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm mb-4 flex items-start space-x-2 ${
          message.type === 'success'
            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
            : 'bg-red-500/10 text-red-400 border border-red-500/20'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Backups worden automatisch opgeslagen bij elke export. Je kunt hier ook handmatig een backup maken of oude backups terugzetten.
      </p>

      <button
        type="button"
        onClick={createBackup}
        disabled={creating}
        className="w-full flex items-center justify-center px-3.5 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 mb-4"
      >
        {creating ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Backup maken...
          </>
        ) : (
          <>
            <Save className="h-4 w-4 mr-2" />
            Nu backup maken
          </>
        )}
      </button>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : backups.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Geen backups gevonden</p>
          <p className="text-sm mt-1">Exporteer je data om een backup te maken</p>
        </div>
      ) : (
        <div className="space-y-2">
          {backups.map((backup) => (
            <div
              key={backup.id}
              className="bg-gray-700/50 border border-gray-600 rounded-lg p-4 hover:bg-gray-700/70 transition-colors duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Database className="h-4 w-4 text-blue-400" />
                    <span className="text-sm font-medium text-white">
                      {formatDate(backup.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">{backup.description}</p>
                  <p className="text-xs text-gray-500">Grootte: {formatSize(backup.backup_size)}</p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() => downloadBackup(backup.id)}
                    className="p-2 text-blue-400 hover:text-blue-300 transition-colors duration-200"
                    title="Download backup"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => restoreBackup(backup.id)}
                    disabled={restoring === backup.id}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 disabled:opacity-50 whitespace-nowrap"
                  >
                    {restoring === backup.id ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      'Deze backup terugzetten'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteBackup(backup.id)}
                    disabled={deleting === backup.id}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors duration-200 disabled:opacity-50"
                    title="Verwijder backup"
                  >
                    {deleting === backup.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mt-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-300">
            <p className="font-medium mb-1">Let op:</p>
            <ul className="list-disc list-inside space-y-0.5 text-blue-300/80">
              <li>Backups worden automatisch aangemaakt bij elke data export</li>
              <li>Herstel voegt data toe aan je bestaande data (verwijdert geen bestaande data)</li>
              <li>Je kunt backups downloaden als JSON bestand</li>
              <li>Verwijderde backups kunnen niet worden hersteld</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
